import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import * as authApi from '../api/auth';
import { deleteSecureItem, getSecureItem, setSecureItem } from '../storage';
import { PosterKind, Role, User } from '../types';

const TOKEN_KEY = 'reviewcoin_token';
const ACTIVE_MODE_KEY = 'reviewcoin_active_mode';

/** Which of the account's capabilities the app is currently acting as —
 * only meaningful for non-admin accounts. A dual-role account switches this
 * from the Profile screen instead of needing two separate logins. */
type ActiveMode = 'REVIEWER' | 'SHOP_OWNER';

interface AuthContextValue {
  isLoading: boolean;
  token: string | null;
  user: User | null;
  activeMode: ActiveMode;
  setActiveMode: (mode: ActiveMode) => void;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    name: string,
    role: Role,
    posterKind?: PosterKind,
    referralCode?: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  addRole: (role: Role, posterKind?: PosterKind) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [activeMode, setActiveModeState] = useState<ActiveMode>('REVIEWER');

  const setActiveMode = useCallback((mode: ActiveMode) => {
    setActiveModeState(mode);
    setSecureItem(ACTIVE_MODE_KEY, mode).catch(() => {});
  }, []);

  /** Picks which mode to land in: the last one the user actively chose, if
   * their account still holds that capability; otherwise their primary
   * registered role. Runs after every login/register/refresh so a role
   * added elsewhere (or lost, in theory) is reflected immediately. */
  const applyActiveModeFor = useCallback(async (freshUser: User) => {
    const roles = freshUser.roles ?? [freshUser.role];
    const stored = await getSecureItem(ACTIVE_MODE_KEY);
    if ((stored === 'REVIEWER' || stored === 'SHOP_OWNER') && roles.includes(stored)) {
      setActiveModeState(stored);
      return;
    }
    setActiveModeState(freshUser.role === 'SHOP_OWNER' ? 'SHOP_OWNER' : 'REVIEWER');
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const stored = await getSecureItem(TOKEN_KEY);
        if (stored) {
          const freshUser = await authApi.me(stored);
          setToken(stored);
          setUser(freshUser);
          await applyActiveModeFor(freshUser);
        }
      } catch {
        await deleteSecureItem(TOKEN_KEY);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [applyActiveModeFor]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await authApi.login(email, password);
      await setSecureItem(TOKEN_KEY, res.accessToken);
      setToken(res.accessToken);
      // The login/register response only carries a minimal user shape — fetch
      // the full profile (tier/fee info) right away so screens never show stale
      // 0%/undefined tier data before the first manual refreshUser() call.
      const freshUser = await authApi.me(res.accessToken);
      setUser(freshUser);
      await applyActiveModeFor(freshUser);
    },
    [applyActiveModeFor],
  );

  const registerFn = useCallback(
    async (
      email: string,
      password: string,
      name: string,
      role: Role,
      posterKind?: PosterKind,
      referralCode?: string,
    ) => {
      const res = await authApi.register(email, password, name, role, posterKind, referralCode);
      await setSecureItem(TOKEN_KEY, res.accessToken);
      setToken(res.accessToken);
      const freshUser = await authApi.me(res.accessToken);
      setUser(freshUser);
      await applyActiveModeFor(freshUser);
    },
    [applyActiveModeFor],
  );

  const logout = useCallback(async () => {
    // Best-effort: revoke the token server-side so it can't be replayed if
    // it leaked (shared/lost device). If the request fails (offline, etc.)
    // still clear local state — the user must not get stuck unable to log
    // out just because the network call didn't go through.
    if (token) {
      await authApi.logout(token).catch(() => {});
    }
    await deleteSecureItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
  }, [token]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    const freshUser = await authApi.me(token);
    setUser(freshUser);
  }, [token]);

  /** Adds the OTHER role capability to this account (see backend
   * AuthService.addRole) and switches straight into it — no re-login. */
  const addRoleFn = useCallback(
    async (role: Role, posterKind?: PosterKind) => {
      if (!token) return;
      const res = await authApi.addRole(token, role, posterKind);
      await setSecureItem(TOKEN_KEY, res.accessToken);
      setToken(res.accessToken);
      setUser(await authApi.me(res.accessToken));
      setActiveMode(role === 'SHOP_OWNER' ? 'SHOP_OWNER' : 'REVIEWER');
    },
    [token, setActiveMode],
  );

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        token,
        user,
        activeMode,
        setActiveMode,
        login,
        register: registerFn,
        logout,
        refreshUser,
        addRole: addRoleFn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
